import { useQuery } from '@apollo/client'
import { BodyText } from '@paypalcorp/pp-react'
import { navigate } from '@reach/router'
import { merge } from 'lodash-es'
import PageMessage from 'ppbl/components/PageMessage'
import { getApplicationByIdPollingQuery } from 'ppbl/graphql/queries'
import { useApplicationContext } from 'ppbl/hooks'
import clientLogger from 'ppbl/utils/client-logger'
import { trackPage } from 'ppbl/utils/fpti-helper'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import serverData from 'ppbl/utils/server-data'
import {
  getApplicationByIdPollingPayloadGenerator,
  handleGraphqlError,
} from 'ppbl/screens/__utils__'
import React, { useEffect, useRef } from 'react'
import { CaseTypes } from 'ppbl/utils/constants'

type Props = {
  applicationType: string
}

const fptiData = {
  page: 'submissionLoadingPage',
}

const SubmissionLoading: React.FC<Props> = ({ applicationType }) => {
  const { data, updateApplicationContext } = useApplicationContext()

  const pollingStartRef = useRef(false)

  const renewalToken = serverData.startParams?.renewalToken

  const {
    loading,
    error,
    data: pollingData,
    startPolling,
    stopPolling,
  } = useQuery(getApplicationByIdPollingQuery, {
    variables: {
      applicationId: getApplicationByIdPollingPayloadGenerator(
        data?.applicationMetaData?.applicationId ||
          sessionStorage.getItem('appId'),
      ),
    },
  })

  useEffect(() => {
    clientLogger.info(
      'Loading page is loaded - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
    trackPage(
      merge(fptiData, {
        applicationId: data?.applicationMetaData?.applicationId,
      }),
    )
    sessionStorage.setItem('mostRecentCompletedPage', 'loading')
    if (pollingStartRef.current === false) {
      startPolling(serverData.data.appConfig.application.waitingScreenPolling)
      pollingStartRef.current = true
    }
  }, [])

  useEffect(() => {
    if (error) {
      stopPolling()
      handleGraphqlError(
        error,
        'Error while polling GraphQl call on offer loading page',
        'ApplicationPage 2 - updateApplicationMutation',
        data?.applicationMetaData?.applicationId,
        renewalToken,
        data?.applicationMetaData?.legalBusinessName,
        applicationType,
      )
    } else if (!loading) {
      const {
        credit: {
          businessCredit: {
            application: { statusReasons, portalAccess },
          },
        },
      } = pollingData

      const applicationSubmitStatusReason =
        statusReasons[0] || 'NO_STATUS_FOUND'

      if (applicationSubmitStatusReason !== 'EVALUATION_IN_PROGRESS') {
        stopPolling()
        updateApplicationContext({
          applicationMetaData: {
            ...data.applicationMetaData,
            applicationSubmitStatus: applicationSubmitStatusReason,
            portalAccess: merge(
              data.applicationMetaData.portalAccess,
              portalAccess,
            ),
          },
        })
        sessionStorage.setItem(
          'applicationSubmitStatus',
          applicationSubmitStatusReason,
        )
        switch (applicationSubmitStatusReason) {
          case CaseTypes.CREDIT_BUREAU_FROZEN:
            navigate(
              `/merchant-lending/loanbuilder/application/us/${applicationType}/credit-frozen`,
            )
            break
          case CaseTypes.FRAUD_ALERT:
            navigate(
              `/merchant-lending/loanbuilder/application/us/${applicationType}/pending`,
            )
            break
          case CaseTypes.SYSTEM_FAILURE:
            navigate(
              `/merchant-lending/loanbuilder/application/us/${applicationType}/pending`,
            )
            break
          case CaseTypes.PENDING_STIPULATIONS:
            navigate(
              `/merchant-lending/loanbuilder/application/us/${applicationType}/offer`,
            )
            break
          default:
            navigate(
              `/merchant-lending/loanbuilder/application/us/${applicationType}/ineligible`,
            )
            break
        }
      }
    }
  }, [loading, pollingData])

  return (
    <PageMessage
      type="loading"
      localization={{
        title: getLocalizedText('submissionLoading-page.title'),
      }}
    >
      <BodyText className="body__text">
        {getLocalizedText('submissionLoading-page.bodyText')}
      </BodyText>
    </PageMessage>
  )
}

export default SubmissionLoading
